// src/components/PATandBayes.jsx
import React from "react";

export default function PATandBayes() {
  return (
    <section
      className="mt-4 text-gray-300 space-y-6 leading-relaxed text-sm"
      aria-labelledby="pat-bayes-heading"
    >
      <h2
        id="pat-bayes-heading"
        className="text-lg font-bold text-gray-100 mb-2"
      >
        PAT, Causality and Bayes
      </h2>

      <p>
        Probability Analysis Theory treats causality as a structured domain of
        interpretation. In PAT, causal claims must be made explicit,
        structurally framed, and epistemically constrained before causal
        analysis can proceed.
      </p>

      <div>
        <h3 className="font-semibold text-gray-200 mt-4 mb-1">
          Causality in PAT
        </h3>
        <p>
          PAT is concerned with how causal interpretations are formed,
          stabilised, and communicated under uncertainty. It does not infer
          causal truth from correlational patterning alone. Instead, it requires
          explicit variables, directional assumptions, and authorised causal
          structure so that intervention, explanation, and accountability remain
          analytically separable.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-200 mt-4 mb-1">
          Pearlian structure
        </h3>
        <p>
          Pearl’s structural causal model provides the formal foundation for
          this approach. Directed relations, DAG-based structure, and the
          distinction between association, intervention, and counterfactual
          reasoning anchor PAT’s causal layer. PAT does not replace Pearlian
          causality; it builds an interpretive surface around it.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-200 mt-4 mb-1">
          BCSM: Bayesian Causal Stability Metric
        </h3>
        <p>
          The Bayesian Causal Stability Metric (BCSM) is a bounded scalar of
          causal interpretive stability, not a probability of real-world
          causation. It expresses how stable a causal interpretation is, given
          structural coherence, linguistic causal signal, and structural and
          textual scaffold within the analysed input.
        </p>
        <p>
          BCSM does not estimate causal effect size, does not imply causal
          identification, and does not function as a truth metric. Its role is
          to communicate whether the system’s authorised causal reading is
          stable enough to be treated as coherent under stated structural and
          contextual constraints.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-200 mt-4 mb-1">
          ESCp: Epistemic Stability Coefficient
        </h3>
        <p>
          ESCp is an externally assigned epistemic coefficient that modulates
          BCSM according to domain norms, methodological constraints, and
          background trust. It stabilises or attenuates the interpretive value
          post hoc, without overriding causal structure or supplying empirical
          confirmation.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-200 mt-4 mb-1">
          How Classic Bayes is applied
        </h3>
        <p>
          PAT uses Bayesian form in a presentational and communicative sense. It
          helps users understand the stability of a causal language
          interpretation without conflating semantic coherence with causal
          truth, effect magnitude, or statistically identified causation.
        </p>
      </div>

      <ul className="list-disc list-inside space-y-2">
        <li>Causality in PAT is explicitly structured, not implied.</li>
        <li>Pearl provides the formal causal foundation for that structure.</li>
        <li>
          BCSM expresses the epistemic stability of a causal interpretation.
        </li>
        <li>
          ESCp modulates that stability by domain-sensitive epistemic norms.
        </li>
        <li>
          None of these elements, by themselves, assert causal truth or effect
          size.
        </li>
      </ul>

      <p>
        Together, causality, Pearlian structure, BCSM, and ESCp allow PAT to
        express whether a causal reading is available, and how responsible that
        interpretation is viewed under explicit structural and epistemic
        constraints.
      </p>
    </section>
  );
}
